import { useState, useEffect } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { sessionService } from '../../services/sessionService';
import DataTable, { type DataTableColumn } from '../data-display/DataTable';
import { SessionStatus, MessageRole } from '../../types/session';
import type { AgentSessionDto, AgentMessageDto } from '../../types/session';

const statusLabels: Record<number, string> = {
  [SessionStatus.Active]: 'Active',
  [SessionStatus.Paused]: 'Paused',
  [SessionStatus.Completed]: 'Completed',
  [SessionStatus.Cancelled]: 'Cancelled',
  [SessionStatus.Expired]: 'Expired',
  [SessionStatus.Error]: 'Error',
};

const roleLabels: Record<number, string> = {
  [MessageRole.System]: 'System',
  [MessageRole.User]: 'User',
  [MessageRole.Assistant]: 'Assistant',
  [MessageRole.Tool]: 'Tool',
};

interface Props {
  agentId: string;
  agentSlug: string;
}

export const ResponsesTab = ({ agentId }: Props) => {
  const [sessions, setSessions] = useState<AgentSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<AgentSessionDto | null>(null);
  const [messages, setMessages] = useState<AgentMessageDto[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);

  useEffect(() => {
    loadSessions();
  }, [agentId]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const result = await sessionService.getSessions({
        agentDefinitionId: agentId,
        pageSize: 100,
        searchTerm: 'Responses',
      });
      setSessions(result.items ?? []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const viewMessages = async (session: AgentSessionDto) => {
    setSelectedSession(session);
    setMsgLoading(true);
    try {
      const msgs = await sessionService.getMessages(session.id);
      setMessages(msgs);
    } catch {
      setMessages([]);
    } finally {
      setMsgLoading(false);
    }
  };

  const columns: DataTableColumn<AgentSessionDto>[] = [
    { id: 'sessionName', label: 'Session', render: (r) => <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>{r.sessionName}</Typography> },
    { id: 'messageCount', label: 'Messages', render: (r) => r.messageCount ?? 0 },
    {
      id: 'status', label: 'Status',
      render: (r) => <Chip label={statusLabels[r.status] ?? r.status} size="small" color={r.status === SessionStatus.Active ? 'success' : 'default'} variant="outlined" />,
    },
    {
      id: 'createdAt', label: 'Created',
      render: (r) => new Date(r.createdAt).toLocaleString(),
    },
    {
      id: 'actions', label: '', align: 'right',
      render: (r) => (
        <Tooltip title="View messages">
          <IconButton size="small" onClick={() => viewMessages(r)}><ViewIcon fontSize="small" /></IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        Sessions created via the Responses API for this agent.
      </Typography>
      <DataTable columns={columns} rows={sessions} loading={loading} emptyMessage="No Responses API sessions found." />

      <Dialog open={!!selectedSession} onClose={() => setSelectedSession(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Messages — {selectedSession?.sessionName}
        </DialogTitle>
        <DialogContent>
          {msgLoading ? (
            <Typography>Loading...</Typography>
          ) : messages.length === 0 ? (
            <Typography color="text.secondary">No messages.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {messages.map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: m.role === MessageRole.Assistant
                      ? 'var(--glass-bg-subtle)'
                      : 'transparent',
                    border: '1px solid var(--glass-border)',
                  }}
                >
                  <Typography variant="caption" color="primary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                    {roleLabels[m.role] ?? m.role}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {m.content}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
